import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  buscarPrazosOficina,
  FiltrosOficina,
  MotivoParado,
  RespostaPrazos,
  SituacaoPrazo,
  VeiculoParado,
  VeiculoPrazo,
} from '../../../api/dashboardOficinaApi';
import DetalheTabelaModal from '../../../shared/ui/DetalheTabelaModal';
import TabelaPaginada from '../../../shared/ui/TabelaPaginada';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { notify } from '../../../shared/feedback/notify';
import { corDaSerie, formatarNumero, TINTA_GRAFICO } from '../../../shared/ui/chart-palette';
import {
  AreaEstado,
  CardGrafico,
  EIXO_LINHA,
  EIXO_TICK,
  ESTILO_TOOLTIP,
  Kpi,
  Legenda,
} from '../../../shared/ui/GraficoBase';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/dashboard-base.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Aba Prazos (at-88) — o pátio AGORA.
 *
 * ⚠️ É a única aba do painel que não respeita o período da tela, e isso é decisão de conteúdo, não
 * limitação: o veículo mais atrasado costuma ser justamente o que entrou antes da janela escolhida.
 * A faixa de aviso sai de `filtrosNaoAplicados`, que o backend declara — sem ela, quem aplica um
 * mês vê o mesmo número e conclui que naquele mês não havia nada atrasado.
 *
 * ⚠️ Não confundir com o "Cumprimento de prazo" da aba Tempos & Equipe: aquele é histórico, conta
 * só etapas que já terminaram, e por isso não enxerga o atraso em curso. São as duas metades da
 * mesma pergunta, e o drawer explica a diferença.
 */

/*
 * ⚠️ Aqui a cor é SEMÂNTICA, não identidade de série: verde/amarelo/vermelho é o semáforo que o
 * leitor já conhece, e trocar essa convenção por "slot 1, 2, 3" tornaria o gráfico ilegível de
 * relance — que é o único uso desta aba. Os valores continuam saindo da paleta validada; o que
 * muda é o critério de escolha, e é por isso que os slots não são os primeiros.
 *
 * "Sem prazo" usa o NEUTRO de propósito: é ausência de informação, não um degrau entre bom e ruim.
 * Pintá-lo de uma cor do semáforo o colocaria numa escala a que ele não pertence.
 */
const COR_ESTOURADO = corDaSerie(7); // vermelho
const COR_RISCO = corDaSerie(3); // amarelo
const COR_DENTRO = corDaSerie(5); // verde
const COR_SEM_PRAZO = TINTA_GRAFICO.neutro;

/** Rótulos exibidos para cada motivo aparente de parada. */
const MOTIVOS: Record<MotivoParado, string> = {
  SEM_LIBERACAO: 'Aguardando liberação',
  SEM_COLABORADOR: 'Sem colaborador vinculado',
  SERVICO_INACABADO: 'Serviço deixado inacabado',
  SESSAO_PAUSADA: 'Atividade pausada',
  SEM_MOTIVO_APARENTE: 'Sem motivo aparente',
};

const SITUACOES: Record<SituacaoPrazo, string> = {
  DENTRO: 'Dentro do prazo',
  EM_RISCO: 'Perto de estourar',
  ESTOURADO: 'Prazo estourado',
  SEM_PRAZO: 'Sem prazo definido',
};

export const helpSectionsPrazos: ComoFuncionaSecao[] = [
  {
    titulo: 'Esta aba mostra o agora, não um período',
    conteudo: (
      <>
        <p>
          Tudo aqui descreve <strong>o pátio neste momento</strong>: quais veículos estão parados
          além do prazo da etapa em que se encontram, quais estão perto disso e quais não recebem
          trabalho há dias.
        </p>
        <p>
          Por isso o <strong>período</strong> e a <strong>situação</strong> escolhidos em Filtros
          não valem nesta aba — e a tela avisa quando você os aplica. O motivo é prático: o veículo
          mais atrasado costuma ser exatamente o que entrou antes do período que você escolheu, e
          filtrar por data o esconderia. Esteira, classe e tipo de dano continuam valendo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Como o prazo da etapa é calculado',
    conteudo: (
      <>
        <p>
          O prazo é o que o Chefe de Oficina definiu ao mover o veículo para a etapa —{' '}
          <strong>a previsão de dias no setor</strong>, a mesma que aparece nas telas de operação.
          A contagem começa na última mudança de esteira, não na entrada do veículo na oficina.
        </p>
        <p>
          Em <strong>Aguardando</strong>, o prazo exibido soma a maior espera de peça pendente, do
          mesmo jeito que as outras telas fazem. É por isso que um veículo ali pode ter prazo maior
          do que o cadastrado na etapa.
        </p>
        <p>
          <strong>Perto de estourar</strong> começa quando o veículo já consumiu 80 por cento do
          prazo. É um critério desta tela, escolhido para dar tempo de remanejar alguém — não é o
          mesmo do alerta automático, que avisa um dia antes de estourar.
        </p>
      </>
    ),
  },
  {
    titulo: 'Sem prazo definido não é prazo cumprido',
    conteudo: (
      <>
        <p>
          Um veículo aparece como <strong>sem prazo definido</strong> quando a etapa em que ele
          está não tem previsão cadastrada. Não dá para dizer se ele está atrasado ou adiantado — e,
          mais importante, <strong>nenhum alerta do sistema o cobre</strong>.
        </p>
        <p>
          Por isso ele nunca é somado aos que estão dentro do prazo: seria transformar ausência de
          informação em tranquilidade. Quando esse número estiver alto, o caminho é definir a
          previsão ao mover o veículo.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que conta como veículo parado',
    conteudo: (
      <>
        <p>
          Entra na lista o veículo em <strong>etapa de trabalho</strong> que está há três dias ou
          mais sem nenhum registro de atividade — ninguém iniciou nem finalizou trabalho nele nesse
          intervalo.
        </p>
        <p>
          Quem tem <strong>atividade em andamento</strong> nunca entra, por mais tempo que esteja na
          etapa: alguém está com a mão no carro agora. Já <strong>atividade pausada</strong> conta,
          porque pausada significa que ninguém está trabalhando.
        </p>
        <p>
          Etapas como Aguardando e Aguardando Entrega ficam de fora: são filas por definição, e
          listá-las encheria a tela do que é normal.
        </p>
      </>
    ),
  },
  {
    titulo: 'O motivo é um indício, não um diagnóstico',
    conteudo: (
      <>
        <p>
          O motivo ao lado de cada veículo parado é <strong>derivado do que está registrado</strong>{' '}
          no sistema: falta liberação administrativa, ninguém vinculado, serviço deixado inacabado
          ou atividade pausada.
        </p>
        <p>
          A causa real pode ser outra — peça a caminho, decisão do cliente, negociação — e o painel
          não a enxerga. Use o motivo para saber por onde começar a olhar, não como conclusão.
        </p>
        <p>
          Quando mais de um motivo se aplica, é exibido o que precisa ser destravado primeiro: sem
          liberação o veículo não anda de jeito nenhum, e sem colaborador ninguém pode começar.
        </p>
      </>
    ),
  },
  {
    titulo: 'Prazo da etapa e prazo de entrega são coisas diferentes',
    conteudo: (
      <>
        <p>
          <strong>Prazo da etapa</strong> é o combinado para a esteira atual, e reinicia a cada
          mudança de esteira.
        </p>
        <p>
          <strong>Prazo de entrega</strong> é o prazo de 90 dias do veículo na oficina, contado
          desde a entrada e descontando os dias em oficina externa. Um veículo pode estar dentro do
          prazo da etapa e ainda assim com o prazo de entrega perto de acabar.
        </p>
      </>
    ),
  },
];

interface Props {
  filtros: FiltrosOficina;
  sidebarRef?: React.RefObject<HTMLElement>;
}

const PrazosTab: React.FC<Props> = ({ filtros, sidebarRef }) => {
  const [dados, setDados] = useState<RespostaPrazos | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  /** Indicador aberto no detalhamento por clique. */
  const [foco, setFoco] = useState<{ titulo: string; apoio: string; placas: string[] } | null>(
    null,
  );
  /** Esteira aberta pelo clique no gráfico. */
  const [esteiraFoco, setEsteiraFoco] = useState<string | null>(null);
  /**
   * Situação selecionada dentro do modal da esteira.
   *
   * ⚠️ O modal abre na situação da FAIXA clicada, não sempre na primeira aba: quem clica na fatia
   * vermelha quer os estourados daquela esteira, e cair em "Dentro do prazo" faria a impressão de
   * que o clique não fez nada.
   */
  const [abaPrazo, setAbaPrazo] = useState<SituacaoPrazo>('ESTOURADO');

  const carregar = useCallback(async (f: FiltrosOficina) => {
    setCarregando(true);
    try {
      const resposta = await buscarPrazosOficina(f);
      /*
       * ⚠️ Resposta fora do formato é FALHA, não dado — mesma guarda das outras abas. Acontece
       * quando o servidor em execução é anterior à versão da tela; sem ela a leitura estoura em
       * `undefined.algo` e derruba a página, que é pior que número faltando.
       */
      if (!resposta || typeof resposta.veiculos === 'undefined') {
        throw new Error('Resposta do servidor em formato inesperado');
      }

      setDados(resposta);
      setErro(null);
    } catch (e) {
      setDados(null);
      setErro(
        'Não foi possível carregar a situação de prazos. Enquanto este aviso estiver na tela, não há número para ler aqui — recarregue a página; se persistir, acione a TI.',
      );
      notify.errorFrom(
        e,
        'Não foi possível carregar a situação de prazos. Recarregue a página; se persistir, acione a TI.',
        { toastId: 'dashboard-oficina-prazos-falhou' },
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar(filtros);
  }, [filtros, carregar]);

  const respondeu = dados !== null;
  const primeiraCarga = carregando && !respondeu;
  /*
   * ⚠️ A frase do vazio depende de a consulta ter RESPONDIDO. "Nenhum veículo com prazo estourado"
   * é uma conclusão de negócio — só pode aparecer quando o servidor respondeu. Falhou, a tela diz
   * que não conseguiu verificar (regra da at-60).
   */
  const textoVazio = respondeu
    ? 'Nenhum veículo nesta situação agora.'
    : 'Não foi possível carregar — veja o aviso acima.';

  const veiculos = useMemo(() => dados?.veiculos ?? [], [dados]);
  const parados = useMemo(() => dados?.parados ?? [], [dados]);

  /*
   * ⚠️ O `?? []` fica DENTRO do `useMemo`: fora dele, o array vazio é referência nova a cada
   * render e a dependência muda sempre, anulando a memoização (o lint pega isso).
   */
  const dadosGrafico = useMemo(
    () =>
      (dados?.porEsteira ?? []).map((e) => ({
        esteira: e.esteira,
        Estourado: e.estourado,
        'Perto de estourar': e.emRisco,
        'Dentro do prazo': e.dentroDoPrazo,
        'Sem prazo': e.semPrazo,
      })),
    [dados],
  );

  const veiculosDaEsteira = useMemo(
    () => (esteiraFoco ? veiculos.filter((v) => v.esteira === esteiraFoco) : []),
    [esteiraFoco, veiculos],
  );

  /*
   * Abas do modal da esteira, uma por situação de prazo.
   *
   * ⚠️ O rótulo carrega a CONTAGEM: sem ela, quem abre precisa entrar em cada aba para descobrir
   * quais têm veículo, e uma aba vazia parece defeito em vez de "nenhum nesta situação".
   */
  const abasDaEsteira = useMemo(
    () =>
      (['ESTOURADO', 'EM_RISCO', 'SEM_PRAZO', 'DENTRO'] as SituacaoPrazo[]).map((s) => ({
        id: s,
        rotulo: `${SITUACOES[s]} (${veiculosDaEsteira.filter((v) => v.situacao === s).length})`,
      })),
    [veiculosDaEsteira],
  );

  const veiculosDaAba = useMemo(
    () => veiculosDaEsteira.filter((v) => v.situacao === abaPrazo),
    [veiculosDaEsteira, abaPrazo],
  );

  /** Clique numa faixa: abre a esteira JÁ na situação daquela faixa. */
  const abrirEsteira = (nome: string | undefined, situacao: SituacaoPrazo) => {
    if (!nome) return;
    setAbaPrazo(situacao);
    setEsteiraFoco(nome);
  };

  const abrirIndicador = (titulo: string, apoio: string, placas: string[] | undefined) =>
    setFoco({ titulo, apoio, placas: placas ?? [] });

  /** Linhas do modal de indicador: casa placa -> veículo, para o modal explicar o número. */
  const linhasDoFoco = useMemo(() => {
    if (!foco) return [];
    const porPlaca = new Map(veiculos.map((v) => [v.plate, v]));
    return foco.placas.map(
      (placa) =>
        porPlaca.get(placa) ?? {
          plate: placa,
          model: null,
          esteira: '-',
          esteiraId: '',
          diasNaEtapa: 0,
          prazoDaEtapa: null,
          diasAlemDoPrazo: null,
          situacao: 'SEM_PRAZO' as SituacaoPrazo,
          prazoEntregaRestante: null,
          diasNaOficina: 0,
        },
    );
  }, [foco, veiculos]);

  const colunasVeiculo = [
    { titulo: 'Placa', largura: 110, texto: (v: VeiculoPrazo) => v.plate },
    { titulo: 'Modelo', largura: 180, texto: (v: VeiculoPrazo) => v.model ?? '-' },
    { titulo: 'Esteira', largura: 150, texto: (v: VeiculoPrazo) => v.esteira },
    {
      titulo: 'Na etapa',
      largura: 110,
      texto: (v: VeiculoPrazo) => `${formatarNumero(v.diasNaEtapa)} dias`,
    },
    {
      titulo: 'Prazo da etapa',
      largura: 140,
      texto: (v: VeiculoPrazo) =>
        v.prazoDaEtapa === null ? 'Não definido' : `${formatarNumero(v.prazoDaEtapa)} dias`,
    },
    {
      titulo: 'Situação',
      largura: 160,
      texto: (v: VeiculoPrazo) =>
        v.situacao === 'ESTOURADO' && v.diasAlemDoPrazo !== null
          ? `Estourado há ${formatarNumero(v.diasAlemDoPrazo)} dias`
          : SITUACOES[v.situacao],
    },
    {
      titulo: 'Prazo de entrega',
      largura: 150,
      texto: (v: VeiculoPrazo) =>
        v.prazoEntregaRestante === null
          ? 'Sem previsão'
          : `${formatarNumero(v.prazoEntregaRestante)} dias restantes`,
    },
  ];

  return (
    <div>
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="do-aba-cabecalho">
        <div className="do-aba-titulo">
          Prazos
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      {/*
        A faixa vem do backend (`filtrosNaoAplicados`), não de uma checagem local: é o servidor que
        sabe o que a rota ignorou. Sem ela, quem aplica um período vê o mesmo número e lê isso como
        resposta ("naquele mês não havia atraso").
      */}
      {respondeu && dados.filtrosNaoAplicados.length > 0 && (
        <div className="aviso-de-tela" role="status">
          {dados.filtrosNaoAplicados.join(' e ')}{' '}
          {dados.filtrosNaoAplicados.length > 1 ? 'não se aplicam' : 'não se aplica'} a esta aba —
          ela mostra sempre a situação do pátio agora. Esteira, classe e tipo de dano continuam
          valendo.
        </div>
      )}

      <p className="do-nota-janela">
        Esta aba descreve <strong>o pátio neste momento</strong>. O prazo de cada linha é o da{' '}
        <strong>etapa atual</strong>, contado desde a última mudança de esteira — não é o prazo de
        entrega do veículo.
      </p>

      <div className="do-kpis">
        <Kpi
          rotulo="Prazo estourado"
          valor={respondeu ? formatarNumero(dados.kpis.estouradosAgora) : '-'}
          apoio="Já passaram do prazo da etapa atual"
          carregando={primeiraCarga}
          onDetalhar={
            respondeu
              ? () =>
                  abrirIndicador(
                    'Veículos com prazo estourado',
                    'Passaram do prazo da etapa em que estão agora.',
                    dados.placasPorIndicador.estouradosAgora,
                  )
              : undefined
          }
        />
        <Kpi
          rotulo="Perto de estourar"
          valor={respondeu ? formatarNumero(dados.kpis.emRiscoEtapa) : '-'}
          apoio="Já consumiram 80% do prazo da etapa"
          carregando={primeiraCarga}
          onDetalhar={
            respondeu
              ? () =>
                  abrirIndicador(
                    'Veículos perto de estourar o prazo',
                    'Já consumiram ao menos 80 por cento do prazo da etapa atual.',
                    dados.placasPorIndicador.emRiscoEtapa,
                  )
              : undefined
          }
        />
        <Kpi
          rotulo="Sem prazo definido"
          valor={respondeu ? formatarNumero(dados.kpis.semPrazoDefinido) : '-'}
          apoio="A etapa atual não tem previsão cadastrada"
          carregando={primeiraCarga}
          onDetalhar={
            respondeu
              ? () =>
                  abrirIndicador(
                    'Veículos sem prazo definido',
                    'A etapa em que estão não tem previsão cadastrada, então nenhum alerta os cobre.',
                    dados.placasPorIndicador.semPrazoDefinido,
                  )
              : undefined
          }
        />
        <Kpi
          rotulo="Parados"
          valor={respondeu ? formatarNumero(dados.kpis.paradosSemAtividade) : '-'}
          apoio={
            respondeu
              ? `Sem atividade há ${dados.diasParaConsiderarParado} dias ou mais`
              : 'Sem atividade registrada há dias'
          }
          carregando={primeiraCarga}
        />
        <Kpi
          rotulo="Prazo de entrega em risco"
          valor={respondeu ? formatarNumero(dados.kpis.prazoEntregaEmRisco) : '-'}
          apoio="Restam 7 dias ou menos dos 90 na oficina"
          carregando={primeiraCarga}
          onDetalhar={
            respondeu
              ? () =>
                  abrirIndicador(
                    'Veículos com prazo de entrega em risco',
                    'Restam sete dias ou menos do prazo de 90 dias na oficina.',
                    dados.placasPorIndicador.prazoEntregaEmRisco,
                  )
              : undefined
          }
        />
      </div>

      {/*
        ⚠️ O gráfico vai SOZINHO na faixa, e as duas tabelas abaixo em largura inteira.
        Tabela não divide espaço horizontal (regra do Victor): em meia tela, "Motivo aparente" —
        a coluna que dá o sentido da lista — sai da área visível e vira barra de rolagem.
      */}
      <div className="do-grid-largo">
        <CardGrafico
          titulo="Situação de prazo por esteira"
          descricao="Cada barra é uma esteira; cada faixa, os veículos que estão nela agora. Clique para ver os veículos."
        >
          {dadosGrafico.length === 0 ? (
            <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
          ) : (
            <>
              <Legenda
                itens={[
                  { cor: COR_ESTOURADO, rotulo: 'Estourado' },
                  { cor: COR_RISCO, rotulo: 'Perto de estourar' },
                  { cor: COR_DENTRO, rotulo: 'Dentro do prazo' },
                  { cor: COR_SEM_PRAZO, rotulo: 'Sem prazo' },
                ]}
              />
              {/*
                ⚠️ Altura PROPORCIONAL ao número de esteiras, e `interval={0}` no eixo.
                Com altura fixa de 300px e 9 esteiras, o recharts não tinha espaço para todos os
                rótulos e passava a PULAR categorias — o gráfico ficava com mais barras do que
                nomes, e cada nome parecia identificar a barra errada. Nome de esteira ainda quebra
                em duas linhas, então a banda precisa de folga.
              */}
              <ResponsiveContainer width="100%" height={Math.max(300, dadosGrafico.length * 46)}>
                <BarChart data={dadosGrafico} layout="vertical" margin={{ left: 20, right: 16 }}>
                  <CartesianGrid horizontal={false} {...{ stroke: EIXO_LINHA.stroke }} />
                  <XAxis type="number" tick={EIXO_TICK} axisLine={EIXO_LINHA} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="esteira"
                    width={150}
                    interval={0}
                    tick={EIXO_TICK}
                    axisLine={EIXO_LINHA}
                  />
                  <Tooltip contentStyle={ESTILO_TOOLTIP} />
                  <Bar
                    dataKey="Estourado"
                    stackId="p"
                    fill={COR_ESTOURADO}
                    onClick={(d: { esteira?: string }) => abrirEsteira(d?.esteira, 'ESTOURADO')}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="Perto de estourar"
                    stackId="p"
                    fill={COR_RISCO}
                    onClick={(d: { esteira?: string }) => abrirEsteira(d?.esteira, 'EM_RISCO')}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="Dentro do prazo"
                    stackId="p"
                    fill={COR_DENTRO}
                    onClick={(d: { esteira?: string }) => abrirEsteira(d?.esteira, 'DENTRO')}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="Sem prazo"
                    stackId="p"
                    fill={COR_SEM_PRAZO}
                    onClick={(d: { esteira?: string }) => abrirEsteira(d?.esteira, 'SEM_PRAZO')}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </CardGrafico>
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Veículos sem trabalho registrado"
          descricao={
            respondeu
              ? `Em etapa de trabalho, sem nenhuma atividade há ${dados.diasParaConsiderarParado} dias ou mais. O motivo é um indício do que está registrado, não um diagnóstico.`
              : 'Veículos em etapa de trabalho sem atividade registrada há dias.'
          }
        >
          {primeiraCarga ? (
            <AreaEstado carregando mensagem={textoVazio} />
          ) : (
            <TabelaPaginada
              itens={parados}
              chave={(p) => p.plate}
              vazio={respondeu ? 'Nenhum veículo parado agora.' : textoVazio}
              colunas={[
                { titulo: 'Placa', largura: 110, texto: (p: VeiculoParado) => p.plate },
                { titulo: 'Modelo', largura: 170, texto: (p: VeiculoParado) => p.model ?? '-' },
                { titulo: 'Esteira', largura: 150, texto: (p: VeiculoParado) => p.esteira },
                {
                  titulo: 'Sem atividade há',
                  largura: 150,
                  texto: (p: VeiculoParado) => `${formatarNumero(p.diasSemAtividade)} dias`,
                },
                {
                  titulo: 'Motivo aparente',
                  largura: 220,
                  texto: (p: VeiculoParado) => MOTIVOS[p.motivo],
                },
              ]}
            />
          )}
        </CardGrafico>
      </div>

      <CardGrafico
        titulo="Todos os veículos no pátio, por gravidade"
        descricao="Os que já estouraram o prazo aparecem primeiro, do maior atraso para o menor."
      >
        {primeiraCarga ? (
          <AreaEstado carregando mensagem={textoVazio} />
        ) : (
          <TabelaPaginada
            itens={veiculos}
            chave={(v) => v.plate}
            vazio={respondeu ? 'Nenhum veículo na oficina neste recorte.' : textoVazio}
            colunas={colunasVeiculo}
          />
        )}
      </CardGrafico>

      {foco && (
        <DetalheTabelaModal
          titulo={foco.titulo}
          apoio={foco.apoio}
          itens={linhasDoFoco}
          chave={(v) => v.plate}
          vazio="Nenhum veículo nesta situação."
          colunas={colunasVeiculo}
          onFechar={() => setFoco(null)}
          sidebarRef={sidebarRef}
        />
      )}

      {esteiraFoco && (
        <DetalheTabelaModal
          titulo={`Prazos — ${esteiraFoco}`}
          apoio="Veículos que estão nesta esteira agora, separados pela situação de prazo."
          abas={abasDaEsteira}
          abaAtiva={abaPrazo}
          onTrocarAba={(id) => setAbaPrazo(id as SituacaoPrazo)}
          itens={veiculosDaAba}
          chave={(v) => v.plate}
          vazio="Nenhum veículo nesta situação, nesta esteira."
          colunas={colunasVeiculo}
          onFechar={() => setEsteiraFoco(null)}
          sidebarRef={sidebarRef}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Prazos"
        secoes={helpSectionsPrazos}
      />
    </div>
  );
};

export default PrazosTab;
